import type { FC } from "react";
import type { Doc } from "sharedb/lib/client";
import { useBoolean } from "usehooks-ts";

import type { BlockType, FieldType, WorksheetType } from "../../shared/types/worksheet";
import { STATIC_CONTENT_FIELD_TYPES, isQuestionBlock } from "../../shared/worksheets/constants";

import { TemplateBlockModal } from "./TemplateBlockModal";

import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { PageIcon } from "mds/icons";
import type { DocViewContextType } from "providers/DocViewProvider";

// eslint-disable-next-line react-refresh/only-export-components
export function getTemplateFieldIndex({
  accessId,
  block,
  canViewTemplateModal,
  showResponses,
}: {
  accessId: string;
  block: BlockType | undefined;
  canViewTemplateModal: boolean;
  showResponses: DocViewContextType["showResponses"];
}): number {
  if (!block || !accessId || !canViewTemplateModal || showResponses) {
    return -1;
  }

  // Never show this affordance for static-only blocks.
  if (!isQuestionBlock(block)) {
    return -1;
  }

  const firstNonStaticFieldIndex = (block.f as FieldType[]).findIndex(
    (f) => f && !STATIC_CONTENT_FIELD_TYPES.includes(f.t),
  );

  return firstNonStaticFieldIndex !== -1 ? firstNonStaticFieldIndex : 0;
}

type TemplateBlockButtonProps = {
  block: BlockType;
  doc: Doc<WorksheetType>;
  versionType: "instructor" | "template";
  className?: string;
};

export const TemplateBlockButton: FC<TemplateBlockButtonProps> = ({
  block,
  doc,
  className,
  versionType,
}) => {
  const { value: modalIsShown, setTrue: showModal, setFalse: hideModal } = useBoolean(false);

  return (
    <>
      <Tooltip content={t(`worksheet.template_block_modal.${versionType}.title`)}>
        <SquareButton
          aria-label={t(`worksheet.template_block_modal.${versionType}.title`)}
          className={className}
          kind="tertiary"
          size="s"
          onClick={showModal}
        >
          <PageIcon />
        </SquareButton>
      </Tooltip>
      <TemplateBlockModal
        block={block}
        doc={doc}
        open={modalIsShown}
        versionType={versionType}
        onClose={hideModal}
      />
    </>
  );
};
